import { useCallback } from 'react'
import { IFoldingView, INode } from '@yfiles/yfiles'
import { KeyUtils } from './keyUtils'

/**
 * Hook that provides helpers to capture and restore the expanded/collapsed
 * state of group nodes in a yFiles folding view.
 */
export function useFoldingState(foldingView: IFoldingView | null) {
  // Capture expanded state of group nodes
  const captureExpandedState = useCallback((): Set<string> => {
    if (!foldingView) return new Set<string>()

    const expandedIds = new Set<string>()

    foldingView.graph.nodes.forEach((node: INode) => {
      const tag = (node as any).tag
      let stableId: string | undefined

      if (tag) {
        if (tag.type === 'CAPABILITY') {
          stableId = KeyUtils.forCapabilityGroup(tag.Name, tag.stage)
        } else if (tag.type === 'TEAM') {
          stableId = KeyUtils.forTeamNode(tag.team, tag.parentName, tag.stage)
        } else if (tag.type === 'APPLICATION_GROUP') {
          stableId = KeyUtils.forAppGroup(tag.Name, tag.team, tag.parentName, tag.stage)
        }
      }

      const nid = stableId || tag?.nodeId
      if (foldingView.graph.isGroupNode(node) && nid !== undefined) {
        if (foldingView.isExpanded(node)) {
          expandedIds.add(String(nid))
        }
      }
    })

    return expandedIds
  }, [foldingView])

  // Restore expanded state to group nodes
  const restoreExpandedState = useCallback(
    (expandedIds: Set<string>, targetView?: IFoldingView) => {
      const fv = targetView ?? foldingView
      if (!fv || expandedIds.size === 0) return

      try {
        const nodesToExpand: INode[] = []
        const nodesToCollapse: INode[] = []
        const nodeIdsFound: Set<string> = new Set()

        // First pass: identify all nodes to expand or collapse
        fv.graph.nodes.forEach((node: INode) => {
          const tag = (node as any).tag
          let stableId: string | undefined

          if (tag) {
            if (tag.type === 'CAPABILITY') {
              stableId = KeyUtils.forCapabilityGroup(tag.Name, tag.stage)
            } else if (tag.type === 'TEAM') {
              stableId = KeyUtils.forTeamNode(tag.team, tag.parentName, tag.stage)
            } else if (tag.type === 'APPLICATION_GROUP') {
              stableId = KeyUtils.forAppGroup(tag.Name, tag.team, tag.parentName, tag.stage)
            }
          }

          const nid = stableId || tag?.nodeId
          if (fv.graph.isGroupNode(node) && nid !== undefined) {
            nodeIdsFound.add(String(nid))
            if (expandedIds.has(String(nid))) {
              nodesToExpand.push(node)
            } else {
              nodesToCollapse.push(node)
            }
          }
        })

        // Optional: warn for missing nodes (can be noisy in production)
        expandedIds.forEach((id) => {
          if (!nodeIdsFound.has(id)) {
            // eslint-disable-next-line no-console
            console.warn(`Node with ID ${id} not found in current view!`)
          }
        })

        // Collapse all nodes first
        for (const node of nodesToCollapse) {
          try {
            if (fv.graph.contains(node) && fv.isExpanded(node)) {
              fv.collapse(node)
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`Error collapsing node: ${error}`)
          }
        }

        // Then expand desired nodes
        for (const node of nodesToExpand) {
          try {
            if (fv.graph.contains(node) && !fv.isExpanded(node)) {
              fv.expand(node)
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`Error expanding node: ${error}`)
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error restoring expanded state:', error)
      }
    },
    [foldingView]
  )

  return { captureExpandedState, restoreExpandedState }
}
